import React from "react";
import { motion } from "framer-motion";
import { MessageSquareText, Users, Megaphone, CheckCheck, Send, Bot, TrendingUp } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";

const spark = [{ v: 12 }, { v: 18 }, { v: 15 }, { v: 26 }, { v: 22 }, { v: 34 }, { v: 30 }, { v: 42 }];

/**
 * WhatsApp Automation product hero mock: conversations, team, campaign status,
 * message preview, automation flow, delivery/read. Original E Telecom UI (demo).
 */
export const WhatsAppProductHero = () => {
  return (
    <div className="relative mx-auto w-full max-w-[580px]">
      <div className="absolute -inset-6 rounded-[2rem] bg-[radial-gradient(circle_at_50%_25%,rgba(37,99,235,0.16),transparent_60%)] blur-xl" />
      <div className="relative overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-[0_24px_80px_rgba(2,6,23,0.16)]">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="h-2.5 w-2.5 rounded-full bg-slate-200" />
          <span className="ml-3 text-xs font-semibold text-slate-500">E Telecom · WhatsApp Console</span>
          <span className="ml-auto rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-semibold text-green-600">Connected</span>
        </div>

        <div className="p-4 grid grid-cols-5 gap-3">
          {/* rail */}
          <div className="col-span-2 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <Stat icon={MessageSquareText} label="Sent" value="48.2k" />
              <Stat icon={Users} label="Contacts" value="12.9k" />
            </div>
            <div className="rounded-xl border border-slate-100 p-3">
              <div className="flex items-center justify-between"><span className="text-[10px] font-semibold text-slate-600">Campaign</span><span className="rounded-full bg-blue-50 px-1.5 py-0.5 text-[8px] font-bold text-blue-600">Live</span></div>
              <div className="mt-1 text-[11px] font-semibold text-slate-800">Festive Offer</div>
              <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100"><div className="h-1.5 rounded-full bg-[var(--et-primary)]" style={{ width: "72%" }} /></div>
              <div className="mt-1 text-[9px] text-slate-400">30,684 / 42,600 delivered</div>
            </div>
            <div className="rounded-xl border border-slate-100 p-3">
              <div className="flex items-center justify-between mb-1"><span className="text-[10px] font-semibold text-slate-600">Engagement</span><TrendingUp className="h-3 w-3 text-green-600" /></div>
              <div className="h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                    <defs><linearGradient id="waHeroGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="100%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                    <Area type="monotone" dataKey="v" stroke="#2563EB" strokeWidth={2} fill="url(#waHeroGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* chat preview */}
          <div className="col-span-3 rounded-xl bg-[#ECE5DD]/50 border border-slate-100 p-3 flex flex-col">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-200/60">
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#075E54] text-white"><Bot className="h-3.5 w-3.5" /></span>
              <span className="text-[11px] font-semibold text-slate-700">Automated reply</span>
              <span className="ml-auto text-[9px] text-slate-400">now</span>
            </div>
            <div className="space-y-2 py-2 flex-1">
              <div className="flex justify-start"><div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-white px-2.5 py-1.5 text-[10.5px] text-slate-700 shadow-sm">Hi, I'd like the price list please</div></div>
              <div className="flex justify-end"><div className="max-w-[88%] rounded-2xl rounded-tr-sm bg-[#DCF8C6] px-2.5 py-1.5 text-[10.5px] text-slate-800 shadow-sm">Sure! Here are our plans 📄 Tap below to view.<span className="ml-1 inline-flex align-middle"><CheckCheck className="h-3 w-3 text-blue-500" /></span></div></div>
              <div className="flex justify-end"><span className="rounded-lg border border-[#25D366]/40 bg-white px-2.5 py-1 text-[10px] font-medium text-[#075E54]">View Plans</span></div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200/60">
              <div className="flex-1 rounded-full bg-white px-2.5 py-1.5 text-[10px] text-slate-400">Type a reply</div>
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--et-primary)] text-white"><Send className="h-3 w-3" /></span>
            </div>
          </div>
        </div>
      </div>

      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="absolute -left-4 top-24 rounded-2xl bg-white border border-slate-200 shadow-lg px-3 py-2 flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-50"><Megaphone className="h-3.5 w-3.5 text-green-600" /></span>
        <span className="text-[11px] font-semibold text-slate-700">3 agents active</span>
      </motion.div>
      <motion.div animate={{ y: [0, -8, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: 1 }} className="absolute -right-3 bottom-16 rounded-2xl bg-[var(--et-navy)] shadow-lg px-3 py-2 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full bg-[var(--et-accent)]" />
        <span className="text-[11px] font-semibold text-white">Flow triggered</span>
      </motion.div>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-100 p-2.5">
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(37,99,235,0.1)] text-[var(--et-primary)]"><Icon className="h-3.5 w-3.5" /></span>
    <div className="mt-1.5 text-[9px] font-medium text-slate-400">{label}</div>
    <div className="font-display text-sm font-bold text-slate-900">{value}</div>
  </div>
);
